import supabase from '../lib/supabase';

export const storyService = {
  async createStory(storyData) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      
      // Get the profile for the username
      let author_name = user.email.split('@')[0]; // Default fallback
      
      try {
        const { data: profileData } = await supabase
          .from('profiles_sb2847x9')
          .select('username')
          .eq('id', user.id)
          .single();
          
        if (profileData && profileData.username) {
          author_name = profileData.username;
        }
      } catch (profileError) {
        console.warn('Could not fetch profile, using email username:', profileError);
      }
      
      // Now create the story with author_name
      const { data, error } = await supabase
        .from('stories_sb2847x9')
        .insert([{
          ...storyData,
          author_id: user.id,
          author_email: user.email,
          author_name: author_name, // Include author_name
          visibility: 'private'
        }])
        .select()
        .single();
        
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating story:', error);
      throw error;
    }
  },

  async getUserStories() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('stories_sb2847x9')
        .select('*')
        .eq('author_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching user stories:', error);
      throw error;
    }
  },

  async getPublicStories() {
    try {
      const { data, error } = await supabase
        .from('stories_sb2847x9')
        .select(`
          *,
          profiles_sb2847x9!stories_sb2847x9_author_id_fkey (
            username
          )
        `)
        .eq('visibility', 'public')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform the data to include author_username
      const transformedData = data?.map(story => ({
        ...story,
        author_username: story.profiles_sb2847x9?.username || story.author_name || 'Anonymous'
      })) || [];

      return transformedData;
    } catch (error) {
      console.error('Error fetching public stories:', error);
      throw error;
    }
  },

  async updateStory(storyId, updates) {
    try {
      const { data, error } = await supabase
        .from('stories_sb2847x9')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', storyId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating story:', error);
      throw error;
    }
  },

  async deleteStory(storyId) {
    try {
      // First get all nodes for this story
      const { data: nodesData, error: nodesError } = await supabase
        .from('story_nodes_sb2847x9')
        .select('id')
        .eq('story_id', storyId);
      
      if (nodesError) throw nodesError;
      
      // Delete node positions
      await supabase
        .from('node_positions_sb2847x9')
        .delete()
        .eq('story_id', storyId);
      
      // Delete options if there are nodes
      if (nodesData && nodesData.length > 0) {
        const nodeIds = nodesData.map(node => node.id);
        
        // Delete options where this node is the source
        await supabase
          .from('options_sb2847x9')
          .delete()
          .in('story_node_id', nodeIds);
          
        // Delete options where this node is the target
        await supabase
          .from('options_sb2847x9')
          .delete()
          .in('next_node_id', nodeIds);
      }
      
      // Delete all story nodes
      await supabase
        .from('story_nodes_sb2847x9')
        .delete()
        .eq('story_id', storyId);
      
      // Finally delete the story itself
      const { error } = await supabase
        .from('stories_sb2847x9')
        .delete()
        .eq('id', storyId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting story:', error);
      throw error;
    }
  },

  async updateVisibility(storyId, visibility, shareId = null) {
    try {
      const updates = {
        visibility,
        share_id: shareId,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('stories_sb2847x9')
        .update(updates)
        .eq('id', storyId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating story visibility:', error);
      throw error;
    }
  },

  async generateShareLink(storyId) {
    try {
      // Generate a unique, unguessable share ID
      const shareId = crypto.randomUUID();
      
      const { data, error } = await supabase
        .from('stories_sb2847x9')
        .update({
          share_id: shareId,
          visibility: 'shared',
          updated_at: new Date().toISOString()
        })
        .eq('id', storyId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error generating share link:', error);
      throw error;
    }
  },

  async revokeShareLink(storyId) {
    try {
      const { data, error } = await supabase
        .from('stories_sb2847x9')
        .update({
          share_id: null,
          // Keep visibility as 'shared' so user can generate new links
          visibility: 'shared',
          updated_at: new Date().toISOString()
        })
        .eq('id', storyId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error revoking share link:', error);
      throw error;
    }
  },

  async getStoryByShareId(shareId) {
    try {
      const { data, error } = await supabase
        .from('stories_sb2847x9')
        .select('*')
        .eq('share_id', shareId)
        .eq('visibility', 'shared')
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error getting shared story:', error);
      throw error;
    }
  }
};